import React, {useRef, useState} from "react";
import {Box, Button, Flex, Text, Badge, Stack, Icon, useToast} from "@chakra-ui/react";
import {FiFile, FiUpload} from "react-icons/fi";
import {
  fetchDependentDocuments,
  fetchDependentDocumentUrl,
  uploadDependentDocument,
  messageFor,
} from "../../api/dependents";
import {useDependentSection, SECTION_STATES} from "./useDependentSection";
import DependentShell, {EmptyState, ScopeNote} from "./DependentShell";
import {formatDateTime, formatBytes} from "./format";

// Files on a dependent's record.
//
// Opening one goes through a dependent-scoped signed-URL endpoint that applies
// the SAME narrowing the listing did, re-checked at the moment of the click. A
// listing is a snapshot: the guardianship it was drawn under can have ended,
// aged out or been restricted by the young person since it was fetched.
//
// Uploading goes to `/crudStorageUpload`, the one endpoint that writes to S3.
// The file-name rules, the traversal check, the provenance row that records who
// produced the file, and the clinic's "a document arrived" notification all
// live there already, and it asks the guardianship policy for
// `documents: upload` when the folder is not the caller's own.

export default function DependentDocuments({state}) {
  const section = useDependentSection({
    state,
    section: "documents",
    load: fetchDependentDocuments,
  });
  const items = section.data?.items || [];
  const toast = useToast();
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState(null);

  const mayUpload = !!section.dependent?.capabilities?.documents?.includes("upload");

  const open = async (file) => {
    setOpening(file.fileId);
    try {
      const downloadUrl = await fetchDependentDocumentUrl(
        state,
        section.dependentPatientId,
        file.fileId
      );
      if (!downloadUrl) throw new Error("No link was returned.");
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast({
        status: "error",
        title: "That file could not be opened",
        description: messageFor(error, "It may no longer be available to you."),
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setOpening(null);
    }
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    // Clear immediately, so choosing the same file twice still fires a change.
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    try {
      await uploadDependentDocument(state, section.dependentPatientId, file);
      toast({
        status: "success",
        title: "File uploaded",
        description: `${file.name} has been added to ${section.displayName}'s record and the clinic has been told.`,
        duration: 6000,
        isClosable: true,
      });
      section.reload();
    } catch (error) {
      toast({
        status: "error",
        title: "That file could not be uploaded",
        description: messageFor(error, "Please try again, or contact your clinic."),
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <DependentShell
      title="Files"
      subtitle={`${section.displayName}'s files`}
      displayName={section.displayName}
      accessLevel={section.dependent?.accessLevel}
      status={section.status}
      error={section.error}
      onRetry={section.reload}
      onLeave={section.leave}
      actions={
        mayUpload &&
        section.status === SECTION_STATES.READY && (
          <>
            <Button
              size="sm"
              colorScheme="orange"
              leftIcon={<Icon as={FiUpload} />}
              isLoading={uploading}
              onClick={() => fileInput.current?.click()}
            >
              Upload a file
            </Button>
            <input
              ref={fileInput}
              type="file"
              onChange={upload}
              style={{display: "none"}}
              aria-hidden="true"
            />
          </>
        )
      }
    >
      {section.status === SECTION_STATES.READY &&
        (items.length === 0 ? (
          <EmptyState
            heading="No files"
            body={
              section.data?.recordScope === "own"
                ? "You have not uploaded any files for this person."
                : "There are no files on this record that you can see."
            }
          />
        ) : (
          <>
            <Stack spacing={3}>
              {items.map((file) => (
                <Flex
                  key={file.fileId}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="10px"
                  p={4}
                  bg="white"
                  align="center"
                  gap={3}
                >
                  <Icon as={FiFile} color="gray.400" fontSize="18px" flexShrink={0} />
                  <Box minW={0} flex={1}>
                    <Text fontWeight="600" color="#1a1f36" noOfLines={1}>
                      {file.fileName}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {formatBytes(file.size)} · {formatDateTime(file.lastModified)}
                      {file.folder ? ` · ${file.folder}` : ""}
                    </Text>
                  </Box>
                  {file.uploadedByYou && (
                    <Badge
                      colorScheme="purple"
                      fontSize="10px"
                      textTransform="none"
                      borderRadius="full"
                      px={2}
                      flexShrink={0}
                    >
                      Uploaded by you
                    </Badge>
                  )}
                  <Button
                    size="xs"
                    variant="outline"
                    flexShrink={0}
                    isLoading={opening === file.fileId}
                    onClick={() => open(file)}
                  >
                    Open
                  </Button>
                </Flex>
              ))}
            </Stack>
            <ScopeNote scope={section.data?.recordScope} noun="files" />
          </>
        ))}
    </DependentShell>
  );
}
